import React, { useEffect, useState } from 'react';

interface SteleAltarMonumentProps {
  isActivated: boolean;
  isCurrent: boolean;
  steleIcon: string;
  levelName: string;
  isDestroyed?: boolean;
}

export const SteleAltarMonument: React.FC<SteleAltarMonumentProps> = ({
  isActivated,
  isCurrent,
  steleIcon,
  levelName,
  isDestroyed = false,
}) => {
  // Smooth progressive animation phase when activated (0 to 4)
  const [activationPhase, setActivationPhase] = useState<number>(isActivated ? 4 : 0);

  useEffect(() => {
    if (isActivated) {
      // Step-by-step harmonious, soft awakening sequence
      setActivationPhase(1); // 0s: Warm mending glow / initial resonance
      const t1 = setTimeout(() => setActivationPhase(2), 600); // 0.6s: Stone restores & Runes ignite in turquoise
      const t2 = setTimeout(() => setActivationPhase(3), 1400); // 1.4s: Sacred Blue Diamond ripples expand
      const t3 = setTimeout(() => setActivationPhase(4), 2200); // 2.2s: Vertical celestial beam & aura fully bloom
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    } else {
      setActivationPhase(0);
    }
  }, [isActivated]);

  const active = isActivated || activationPhase > 0;
  // If it was destroyed, once activation begins (phase >= 2) it heals into the restored form
  const visuallyDestroyed = isDestroyed && activationPhase < 2;

  return (
    <div className="relative flex flex-col items-center select-none pointer-events-none transition-all duration-1000">
      {/* 1. Vertical Sacred Light Beacon (Pillar of Azure Light) */}
      {active && (
        <div
          className={`absolute -top-48 w-24 h-72 pointer-events-none transition-all duration-1000 ease-out flex items-center justify-center ${
            activationPhase >= 4 ? 'opacity-85 scale-100' : activationPhase === 3 ? 'opacity-40 scale-90' : 'opacity-0 scale-75'
          }`}
        >
          {/* Core high-intensity vertical beam */}
          <div className="w-8 h-full bg-gradient-to-t from-cyan-400 via-sky-300 to-transparent blur-sm animate-light-pillar" />
          {/* Broad ethereal cyan haze */}
          <div className="absolute w-28 h-full bg-gradient-to-t from-cyan-500/40 via-blue-500/20 to-transparent blur-md" />
        </div>
      )}

      {/* 2. Floating Sparks / Mana Particles rising from the stele */}
      {active && activationPhase >= 3 && (
        <div className="absolute -top-24 w-32 h-36 overflow-hidden pointer-events-none">
          {[
            { left: '15%', delay: '0s', duration: '2.6s', size: 'w-1.5 h-1.5' },
            { left: '40%', delay: '0.4s', duration: '2.2s', size: 'w-2 h-2' },
            { left: '68%', delay: '0.8s', duration: '2.8s', size: 'w-1.5 h-1.5' },
            { left: '30%', delay: '1.2s', duration: '2.4s', size: 'w-1 h-1' },
            { left: '55%', delay: '1.6s', duration: '2.9s', size: 'w-2 h-2' },
            { left: '80%', delay: '1.9s', duration: '2.3s', size: 'w-1.5 h-1.5' },
          ].map((spark, idx) => (
            <div
              key={idx}
              className={`absolute bottom-0 ${spark.size} bg-cyan-300 rounded-full shadow-[0_0_8px_#38bdf8]`}
              style={{
                left: spark.left,
                animation: `floatParticleUp ${spark.duration} ease-in-out infinite`,
                animationDelay: spark.delay,
              }}
            />
          ))}
        </div>
      )}

      {/* 3. The Stele Monolith (Stone Pillar with Amazigh Runes) */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Halo Glow behind the monument when active */}
        <div
          className={`absolute -top-4 w-32 h-40 rounded-full blur-xl transition-all duration-1000 ${
            active
              ? 'bg-cyan-400/45 scale-110 opacity-100'
              : visuallyDestroyed
              ? 'bg-emerald-950/30 scale-90 opacity-40'
              : isCurrent
              ? 'bg-sky-500/20 scale-95 opacity-50'
              : 'opacity-0'
          }`}
        />

        {/* Stele Stone Top Pyramidion */}
        <div
          className={`w-0 h-0 border-l-[18px] border-r-[18px] border-b-[14px] border-l-transparent border-r-transparent transition-all duration-1000 ${
            visuallyDestroyed
              ? 'border-b-stone-700 rotate-6 translate-x-1 translate-y-0.5 opacity-80'
              : active
              ? 'border-b-cyan-300 filter drop-shadow-[0_0_10px_#38bdf8]'
              : 'border-b-slate-700'
          }`}
        />

        {/* Stele Main Monolith Body */}
        <div
          className={`relative w-14 h-24 rounded-t-sm border-2 flex flex-col items-center justify-between p-1.5 transition-all duration-1000 shadow-xl ${
            visuallyDestroyed
              ? 'bg-gradient-to-b from-stone-900 via-stone-850 to-stone-900 border-stone-700/80 shadow-[0_0_15px_rgba(20,83,45,0.4)]'
              : active
              ? 'bg-gradient-to-b from-slate-900 via-cyan-950 to-slate-900 border-cyan-300 shadow-[0_0_25px_rgba(56,189,248,0.5),inset_0_0_12px_rgba(56,189,248,0.4)]'
              : 'bg-gradient-to-b from-slate-800 via-slate-850 to-slate-900 border-slate-750'
          }`}
        >
          {/* Jagged Fracture Lines & Kintsugi Glow for Destroyed / Mending Stele */}
          {visuallyDestroyed && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-15" viewBox="0 0 56 96">
              {/* Main crack */}
              <path
                d="M 12 8 L 24 28 L 18 48 L 34 68 L 26 92"
                fill="none"
                stroke="#14532d"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <path
                d="M 24 28 L 42 36 L 48 50"
                fill="none"
                stroke="#166534"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              {/* Corrupted fumes leaking */}
              <circle cx="24" cy="28" r="3" fill="#22c55e" opacity="0.6" className="animate-ping" />
            </svg>
          )}

          {/* Monolith Corner Accents */}
          <div
            className={`absolute top-1 left-1 w-1.5 h-1.5 border-t border-l transition-colors duration-700 ${
              active ? 'border-cyan-300' : 'border-slate-600'
            }`}
          />
          <div
            className={`absolute top-1 right-1 w-1.5 h-1.5 border-t border-r transition-colors duration-700 ${
              active ? 'border-cyan-300' : 'border-slate-600'
            }`}
          />

          {/* Central Sacred Regional Emblem Floating - Amazigh Yaz ⵣ in radiant illuminated yellow */}
          <div className="relative my-auto flex items-center justify-center">
            {active && (
              <div className="absolute w-12 h-12 rounded-full bg-yellow-400/35 blur-md animate-ping" />
            )}
            <span
              className={`text-2xl font-black transition-all duration-1000 transform select-none ${
                visuallyDestroyed
                  ? 'text-stone-500 opacity-40 grayscale rotate-12 scale-90'
                  : active
                  ? 'text-yellow-300 scale-125 drop-shadow-[0_0_12px_rgba(250,204,21,0.9)] animate-stele-float'
                  : 'text-slate-500 opacity-60 grayscale'
              }`}
              title={levelName}
            >
              {active ? 'ⵣ' : steleIcon}
            </span>
          </div>

          {/* Ancient Amazigh Runes carved in the stone pillar */}
          <div className="w-full flex items-center justify-around px-0.5 border-t border-slate-700/60 pt-1">
            {['ⵣ', 'ⵔ', 'ⵉ', 'ⵢ'].map((glyph, gIdx) => (
              <span
                key={gIdx}
                className={`text-[10px] font-mono font-bold transition-all duration-700 ${
                  active && activationPhase >= 2
                    ? 'text-cyan-300 font-extrabold animate-rune-glow'
                    : visuallyDestroyed
                    ? 'text-stone-600 opacity-30'
                    : 'text-slate-600 opacity-40'
                }`}
                style={{
                  transitionDelay: `${gIdx * 150}ms`,
                }}
              >
                {glyph}
              </span>
            ))}
          </div>
        </div>

        {/* Stele Stepped Stone Pedestal */}
        <div
          className={`w-18 h-2.5 rounded-sm border-t border-x transition-colors duration-700 ${
            active
              ? 'bg-slate-900 border-cyan-400/80 shadow-[0_0_10px_#0284c7]'
              : 'bg-slate-800 border-slate-700'
          }`}
        />
        <div
          className={`w-22 h-3 rounded-sm border-t border-x transition-colors duration-700 ${
            active
              ? 'bg-slate-950 border-cyan-500/90 shadow-[0_0_15px_#38bdf8]'
              : 'bg-slate-900 border-slate-800'
          }`}
        />

        {/* Rubble & Stone Shards on Pedestal if destroyed */}
        {visuallyDestroyed && (
          <div className="absolute -bottom-1 w-20 flex justify-between px-1 pointer-events-none">
            <div className="w-2.5 h-2 bg-stone-700 border border-stone-800 rotate-45 rounded-xs" />
            <div className="w-3 h-2 bg-stone-800 border border-stone-900 -rotate-12 rounded-xs" />
            <div className="w-2 h-1.5 bg-stone-600 rounded-xs" />
          </div>
        )}
      </div>

      {/* 4. THE SACRED BLUE DIAMOND ALTAR (Le Losange Bleu d'Arrivée) */}
      <div className="relative mt-2 flex items-center justify-center">
        {/* Outer Expanding Energy Ripple Waves from the Blue Diamond */}
        {active && activationPhase >= 3 && (
          <>
            <div className="absolute w-10 h-10 border-2 border-cyan-300 rounded-sm animate-diamond-ripple-1 pointer-events-none" />
            <div className="absolute w-10 h-10 border border-sky-400 rounded-sm animate-diamond-ripple-2 pointer-events-none" />
            <div className="absolute w-10 h-10 border border-blue-400 rounded-sm animate-diamond-ripple-3 pointer-events-none" />
          </>
        )}

        {/* The Core Blue Diamond ("Le Losange Bleu") */}
        <div
          className={`relative flex items-center justify-center transition-all duration-1000 ease-out overflow-hidden ${
            active
              ? 'w-11 h-11 bg-gradient-to-br from-cyan-400 via-sky-500 to-blue-700 border-2 border-white rounded-md animate-diamond-core z-20'
              : visuallyDestroyed
              ? 'w-8 h-8 bg-stone-950 border border-stone-800 opacity-60 rounded-sm'
              : isCurrent
              ? 'w-10 h-10 bg-slate-900 border-2 border-cyan-400 shadow-[0_0_15px_#38bdf8] rounded-sm'
              : 'w-7 h-7 bg-slate-950/80 border border-slate-700 rounded-sm'
          }`}
          style={{ transform: 'rotate(45deg)' }}
        >
          {/* Inner Shimmering Diamond Core */}
          <div
            className={`transition-all duration-700 flex items-center justify-center ${
              active
                ? 'w-5 h-5 bg-white/90 rounded-xs shadow-[0_0_10px_#ffffff]'
                : isCurrent
                ? 'w-3 h-3 bg-cyan-300 animate-pulse rounded-xs'
                : 'w-2 h-2 bg-slate-600 rounded-xs'
            }`}
          >
            {/* Center Sacred Glyph inside the Diamond */}
            {active && (
              <span className="text-[9px] font-black text-cyan-900 -rotate-45 select-none">
                ⵣ
              </span>
            )}
          </div>

          {/* Fluid Cyan Energy Fill Sweep */}
          <div
            className="absolute inset-0 bg-gradient-to-t from-cyan-300/60 to-transparent pointer-events-none transition-opacity duration-1000"
            style={{ opacity: active ? 1 : 0 }}
          />
        </div>
      </div>
    </div>
  );
};
